import { type CSSObject, keyframes } from "@emotion/react";
import { Box, useTheme } from "@mui/material";

// Beam travel keyframes. Motion ran the position sweep for 2.5s with a 1s
// `repeatDelay` (a pause between loops). CSS animation-delay only offsets the
// first start, so the per-loop pause is baked into the keyframe instead: the
// beam travels over the first 2.5s (≈71.43% of a 3.5s cycle) and then holds
// off-screen for the remaining 1s before looping.
const TRAVEL_PCT = 71.43; // (2.5s travel / 3.5s total) as a percentage

const sweepHorizontal = keyframes`
  0% { left: -50%; }
  ${TRAVEL_PCT}%, 100% { left: 100%; }
`;
const sweepVertical = keyframes`
  0% { top: -50%; }
  ${TRAVEL_PCT}%, 100% { top: 100%; }
`;
const sweepHorizontalReverse = keyframes`
  0% { right: -50%; }
  ${TRAVEL_PCT}%, 100% { right: 100%; }
`;
const sweepVerticalReverse = keyframes`
  0% { bottom: -50%; }
  ${TRAVEL_PCT}%, 100% { bottom: 100%; }
`;

// Opacity pulse for the beams: [0.3, 0.7, 0.3]. Runs as its own timeline with a
// `mirror` repeat, expressed here as a half-cycle (0.3 -> 0.7) played with
// `alternate` so it mirrors back to 0.3.
const beamOpacityPulse = keyframes`
    from {
        opacity: 0.3;
    }
    to {
        opacity: 0.7;
    }
`;

// Blur pulse for the beams: blur(1px) <-> blur(2.5px), also mirrored.
const beamBlurPulse = keyframes`
    from {
        filter: blur(1px);
    }
    to {
        filter: blur(2.5px);
    }
`;

// Corner-dot opacity pulse: [0.2, 0.4, 0.2], mirrored.
const dotOpacityPulse = keyframes`
    from {
        opacity: 0.2;
    }
    to {
        opacity: 0.4;
    }
`;

// Each beam composes three independent CSS animations (sweep + opacity + blur)
// with the original Motion durations. `alternate` reproduces Motion's `mirror`
// repeatType; the half-durations below are halved from the original because one
// alternate round-trip equals two half-cycles (1.2s pulse => 0.6s each way).
const BEAM_ANIMATION = (sweep: ReturnType<typeof keyframes>, delay: number) =>
  [
    `${sweep} 7s ease-in-out ${delay}s infinite both`,
    `${beamOpacityPulse} 0.6s ease-in-out ${delay}s infinite alternate both`,
    `${beamBlurPulse} 0.75s ease-in-out ${delay}s infinite alternate both`
  ].join(", ");

const DOT_ANIMATION = (duration: number, delay: number) =>
  // mirror => alternate, so each leg is half of the original duration.
  `${dotOpacityPulse} ${duration / 2}s ease-in-out ${delay}s infinite alternate`;

const BEAM_LIGHT = "rgba(var(--mui-palette-primary-mainChannel) / 0.6)";
const BEAM_DARK = "rgba(255, 255, 255, 0.5)";
const DOT_LIGHT = "rgba(var(--mui-palette-primary-mainChannel) / 0.55)";
const DOT_DARK_SOFT = "rgba(255, 255, 255, 0.4)";
const DOT_DARK_STRONG = "rgba(255, 255, 255, 0.6)";

const beamGradient = (angle: 90 | 180, color: string) =>
  `linear-gradient(${angle}deg, transparent, ${color}, transparent)`;

export const AnimatedBorder = () => {
  const theme = useTheme();

  const beamBackground = (angle: 90 | 180): CSSObject => ({
    background: beamGradient(angle, BEAM_LIGHT),
    ...(theme.applyStyles("dark", {
      background: beamGradient(angle, BEAM_DARK)
    }) as CSSObject)
  });

  const dotBackground = (darkColor: string): CSSObject => ({
    background: DOT_LIGHT,
    ...(theme.applyStyles("dark", { background: darkColor }) as CSSObject)
  });

  return (
    <Box
      sx={{
        "@media (prefers-reduced-motion: reduce)": {
          "& *": { animation: "none !important" }
        },
        borderRadius: "inherit",
        inset: "-1px",
        overflow: "hidden",
        pointerEvents: "none",
        position: "absolute",
        zIndex: 1
      }}
    >
      {/* Top beam */}
      <Box
        sx={{
          animation: BEAM_ANIMATION(sweepHorizontal, -0.5),
          ...beamBackground(90),
          height: 2,
          left: 0,
          position: "absolute",
          top: 0,
          width: "50%"
        }}
      />

      {/* Right beam */}
      <Box
        sx={{
          animation: BEAM_ANIMATION(sweepVertical, -2.25),
          ...beamBackground(180),
          height: "50%",
          position: "absolute",
          right: 0,
          top: 0,
          width: 2
        }}
      />

      {/* Bottom beam */}
      <Box
        sx={{
          animation: BEAM_ANIMATION(sweepHorizontalReverse, -4),
          ...beamBackground(90),
          bottom: 0,
          height: 2,
          position: "absolute",
          right: 0,
          width: "50%"
        }}
      />

      {/* Left beam */}
      <Box
        sx={{
          animation: BEAM_ANIMATION(sweepVerticalReverse, -5.75),
          ...beamBackground(180),
          bottom: 0,
          height: "50%",
          left: 0,
          position: "absolute",
          width: 2
        }}
      />

      {/* Top-left dot */}
      <Box
        sx={{
          animation: DOT_ANIMATION(2, 0),
          ...dotBackground(DOT_DARK_SOFT),
          borderRadius: "50%",
          filter: "blur(1px)",
          height: 4,
          left: 0,
          position: "absolute",
          top: 0,
          // Was `width: 2` (a 2x4 ellipse) — corrected to 4 to match the
          // identical bottom-left soft dot (4x4 circle). Static-size typo fix.
          width: 4
        }}
      />

      {/* Top-right dot */}
      <Box
        sx={{
          animation: DOT_ANIMATION(2.4, 0.5),
          ...dotBackground(DOT_DARK_STRONG),
          borderRadius: "50%",
          filter: "blur(2px)",
          height: 7,
          position: "absolute",
          right: 0,
          top: 0,
          width: 7
        }}
      />

      {/* Bottom-right dot */}
      <Box
        sx={{
          animation: DOT_ANIMATION(2.2, 1),
          ...dotBackground(DOT_DARK_STRONG),
          borderRadius: "50%",
          bottom: 0,
          filter: "blur(2px)",
          height: 7,
          position: "absolute",
          right: 0,
          width: 7
        }}
      />

      {/* Bottom-left dot */}
      <Box
        sx={{
          animation: DOT_ANIMATION(2.3, 1.5),
          ...dotBackground(DOT_DARK_SOFT),
          borderRadius: "50%",
          bottom: 0,
          filter: "blur(1px)",
          height: 4,
          left: 0,
          position: "absolute",
          width: 4
        }}
      />
    </Box>
  );
};
